import React, { useState } from "react";
import classNames from "classnames";
import styles from "./QuestionAnswerList.module.scss";
import { ReactComponent as EditIcon } from "../../icons/edit.svg";
import { Plus } from "lucide-react";
import { Tooltip } from "react-tooltip";
import { Field } from "formik";
import Upload from "../UploadInput/Upload";
import TextareaAutosize from "react-textarea-autosize";
import MultiSelectSimple from "../MultiSelectSimple/MultiSelectSimple";
import RadioGroup from "../RadioGroup/RadioGroup";
import { useTranslation } from "react-i18next";

const QuestionAnswerList = ({
  items,
  setFieldValue,
  handleOpenNewFindingModal,
  loadingQuestionId,
}) => {
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState({});

  const getTypeLabel = (type) => {
    switch (type) {
      case "select":
        return "Single select";
      case "multiselect":
        return "Multi select";
      case "file":
        return "File";
      case "text":
      default:
        return "Text";
    }
  };

  const toggleEditMode = (index) => {
    setEditMode((prevEditMode) => ({
      ...prevEditMode,
      [index]: !prevEditMode[index],
    }));
  };

  const formatOptions = (options) => {
    if (!options) return [];
    return options.map((option) => ({
      id: option,
      value: option,
      label: option,
    }));
  };

  const getAnswerLabel = (answer) => {
    if (typeof answer === "object" && answer) {
      return answer.label || answer.value || answer.id || "";
    }
    return answer;
  };

  const renderAnswer = (item, answer) => {
    if (item.answer.type === "text") {
      return (
        <div
          className={styles.answerText}
          dangerouslySetInnerHTML={{
            __html: item.answer.modifyDescription(answer),
          }}
        />
      );
    }

    if (item.answer.type === "file" && typeof answer === "object") {
      return (
        <a
          href={answer.url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.answerFile}
        >
          {answer.filename}
        </a>
      );
    }

    return <span className={styles.answerText}>{getAnswerLabel(answer)}</span>;
  };

  return (
    <div className={styles.list}>
      {items?.map((item, index) => {
        const isEditing = editMode[index];
        const normalizeAnswer = (answer) => {
          if (answer === null || answer === undefined) return null;
          if (typeof answer === "string") {
            const trimmed = answer.trim();
            return trimmed.length > 0 ? trimmed : null;
          }
          return answer;
        };

        const nonNullAnswers = Array.isArray(item.answer.answers)
          ? item.answer.answers
              .map((answer) => normalizeAnswer(answer))
              .filter((answer) => answer !== null)
          : (() => {
              const normalized = normalizeAnswer(item.answer.answers);
              return normalized === null ? [] : [normalized];
            })();
        const hasAnswers = nonNullAnswers.length > 0;
        const canEditEmpty = !hasAnswers;

        return (
          <div
            key={`${item.answer.questionId}-${index}`}
            className={classNames(styles.card, isEditing && styles.editing)}
          >
            <div className={styles.cardHeader}>
              <div className={styles.questionMeta}>
                <div className={styles.questionText}>{item.question}</div>
                <span className={styles.typePill}>
                  {getTypeLabel(item.answer.type)}
                </span>
              </div>

              <div className={styles.actions}>
                {hasAnswers && (
                  <button
                    type="button"
                    className={styles.iconButton}
                    data-tooltip-id={`edit-answer-${index}`}
                    data-tooltip-content={t("auditsModal.auditFormAccordion.editAnswer")}
                    onClick={() => toggleEditMode(index)}
                  >
                    <EditIcon className={styles.editIcon} />
                  </button>
                )}
                <button
                  type="button"
                  className={styles.iconButton}
                  data-tooltip-id={`add-finding-${index}`}
                  data-tooltip-content={t("auditsModal.auditFormAccordion.addNewFinding")}
                  onClick={() => handleOpenNewFindingModal(item.answer.questionId)}
                  aria-label={t("auditsModal.auditFormAccordion.addNewFinding")}
                >
                  {loadingQuestionId === item.answer.questionId ? (
                    <div className={styles.spinner} />
                  ) : (
                    <Plus size={16} className={styles.newFindingIcon} />
                  )}
                </button>
                <Tooltip
                  id={`edit-answer-${index}`}
                  place="top"
                  className={classNames(styles.tooltip, styles.smallTooltip)}
                />
                <Tooltip
                  id={`add-finding-${index}`}
                  place="top"
                  className={classNames(styles.tooltip, styles.smallTooltip)}
                />
              </div>
            </div>

            <div className={styles.answerBlock}>
              <div className={styles.answerLabel}>Answer</div>

              {hasAnswers && !isEditing && (
                <div className={styles.answerContent}>
                  {item.answer.type === "select" || item.answer.type === "multiselect" ? (
                    <div className={styles.answerChips}>
                      {nonNullAnswers.map((answer, idx) => (
                        <span key={idx} className={styles.answerChip}>
                          {getAnswerLabel(answer)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    nonNullAnswers.map((answer, idx) => (
                      <div
                        key={idx}
                        className={classNames(
                          styles.answerRow,
                          item.answer.type === "text" && styles.answerRowText,
                        )}
                      >
                        {renderAnswer(item, answer)}
                      </div>
                    ))
                  )}
                </div>
              )}

              {canEditEmpty || isEditing ? (
                <div className={styles.editorBlock}>
                  {item.answer.type === "text" ? (
                    <Field name={`responses.${item.answer.questionId}`}>
                      {({ field }) => (
                        <TextareaAutosize
                          {...field}
                          className={styles.textInput}
                          minRows={2}
                          placeholder="Type your answer..."
                        />
                      )}
                    </Field>
                  ) : item.answer.type === "file" ? (
                    <Upload
                      questionId={item.answer.questionId}
                      onChange={(filePaths) => {
                        setFieldValue(
                          `responses.${item.answer.questionId}`,
                          filePaths,
                        );
                      }}
                    />
                  ) : item.answer.type === "select" ? (
                    <Field name={`responses.${item.answer.questionId}`}>
                      {({ field }) => (
                        <RadioGroup
                          options={formatOptions(item.answer.options)}
                          selectedValue={field.value}
                          onChange={(e) => {
                            setFieldValue(
                              `responses.${item.answer.questionId}`,
                              e.target.value,
                            );
                          }}
                        />
                      )}
                    </Field>
                  ) : item.answer.type === "multiselect" ? (
                    <Field name={`responses.${item.answer.questionId}`}>
                      {({ field }) => (
                        <MultiSelectSimple
                          options={formatOptions(item.answer.options)}
                          selectedValues={field.value || []}
                          onChangeSelectedValues={(selectedValues) => {
                            setFieldValue(
                              `responses.${item.answer.questionId}`,
                              selectedValues,
                            );
                          }}
                        />
                      )}
                    </Field>
                  ) : null}
                </div>
              ) : null}

              {!hasAnswers && !canEditEmpty && !isEditing && (
                <div className={styles.emptyAnswer}>No answer provided.</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default QuestionAnswerList;
